// Tarifas de Traslado — per-model $/km for the driver extra.
//
// The rate lives on car_models.traslado_mxn_per_km, with car_categories as a
// fallback. The fallback is deliberately NOT filled for Carga/Pasajeros: those
// categories mix vehicles that price very differently (NP300 3.00 vs Hilux 3.50;
// Urvan 3.50 vs a RAV4), so a category default there invents a plausible number
// for a model nobody priced — including, at one point, an ATV at $3.00/km.
// A blank is honest; a wrong number that looks right is not. Rows without a rate
// are shown as "Sin tarifa" and compute_traslado_estimate refuses to price them.
//
// The same screen holds the other two inputs of the traslado quote: km/L per
// model (pre-filled from specs and flagged "Estimado" until someone confirms
// it) and the current $/L per fuel type. Fuel prices are read-only here: they
// come only from the daily CRE sync (sync_fuel_prices — national median,
// Gasolina = Premium), and the table is not writable by staff.
import { React, useState, useEffect } from "react";
import AppWrapper from "../AppWrapper";
import {
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Table,
} from "@mui/material";
import {
  Page,
  Button,
  BlockStack,
  Text,
  LegacyCard,
  TextField,
  Badge,
  Banner,
  Spinner,
  InlineStack,
} from "@shopify/polaris";
import supabase from "../../supabase-client";
import { UserAuth } from "../../context/AuthContext";

function TrasladoRates() {
  // Rates decide what we pay: only whoever authorizes payments (enforced in
  // the DB too, by the car_models/car_categories rate guards).
  const { hasPermission } = UserAuth();
  const allowed = hasPermission("services.pay");
  const [rows, setRows] = useState([]);
  const [edited, setEdited] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [saved, setSaved] = useState(false);
  const [fuels, setFuels] = useState([]); // [{ id, name, price, since }]

  const load = async () => {
    setLoading(true);
    // Only models that actually have cars — the catalog has models we don't own.
    const { data, error: err } = await supabase
      .from("car_models")
      .select(
        "id, name, traslado_mxn_per_km, km_per_liter, km_per_liter_estimated, fuel:fuel_type(name), brand:car_brands(name), category:car_categories(id, name, traslado_mxn_per_km), cars(id)",
      )
      .order("name");
    if (err) {
      setError(err.message);
      setLoading(false);
      return;
    }
    const built = (data || [])
      .map((m) => ({
        id: m.id,
        modelo: m.name,
        marca: m.brand?.name || "",
        categoria: m.category?.name || "",
        categoriaRate: m.category?.traslado_mxn_per_km ?? null,
        rate: m.traslado_mxn_per_km ?? null,
        kmpl: m.km_per_liter ?? null,
        origKmpl: m.km_per_liter ?? null,
        kmplEstimated: !!m.km_per_liter_estimated,
        combustible: m.fuel?.name || "",
        autos: (m.cars || []).length,
      }))
      .filter((m) => m.autos > 0)
      .sort((a, b) => b.autos - a.autos);
    setRows(built);
    setEdited([]);

    const [{ data: types }, { data: prices }] = await Promise.all([
      supabase.from("fuel_type").select("id, name").order("id"),
      supabase
        .from("fuel_prices")
        .select("fuel_type, mxn_per_liter, effective_from, source")
        .order("effective_from", { ascending: false }),
    ]);
    setFuels(
      (types || []).map((t) => {
        const last = (prices || []).find((p) => p.fuel_type === t.id);
        return {
          id: t.id,
          name: t.name,
          price: last ? Number(last.mxn_per_liter) : null,
          since: last?.effective_from || null,
        };
      }),
    );
    setLoading(false);
  };

  useEffect(() => {
    load();
  }, []);

  const changeKmpl = (id, value) => {
    setSaved(false);
    setRows((prev) =>
      prev.map((r) => (r.id === id ? { ...r, kmpl: value } : r)),
    );
    setEdited((prev) => (prev.includes(id) ? prev : [...prev, id]));
  };

  // Confirming an estimated km/L without changing it is a real action too.
  const confirmKmpl = (id) => {
    setSaved(false);
    setRows((prev) =>
      prev.map((r) => (r.id === id ? { ...r, confirmKmpl: true } : r)),
    );
    setEdited((prev) => (prev.includes(id) ? prev : [...prev, id]));
  };

  const changeRate = (id, value) => {
    setSaved(false);
    setRows((prev) =>
      prev.map((r) => (r.id === id ? { ...r, rate: value } : r)),
    );
    setEdited((prev) => (prev.includes(id) ? prev : [...prev, id]));
  };

  const save = async () => {
    setSaving(true);
    setError(null);
    for (const id of edited) {
      const row = rows.find((r) => r.id === id);
      if (!row) continue;
      const raw = String(row.rate ?? "").trim();
      // Empty clears the rate back to "nobody has priced this", which is a
      // legitimate state — better than leaving a stale guess in place.
      const value = raw === "" ? null : Number(raw);
      if (value !== null && (!Number.isFinite(value) || value <= 0)) {
        setError(`Tarifa inválida para ${row.marca} ${row.modelo}: "${raw}"`);
        setSaving(false);
        return;
      }
      const rawK = String(row.kmpl ?? "").trim();
      const kmpl = rawK === "" ? null : Number(rawK);
      if (kmpl !== null && (!Number.isFinite(kmpl) || kmpl <= 0 || kmpl > 40)) {
        setError(`km/L inválido para ${row.marca} ${row.modelo}: "${rawK}"`);
        setSaving(false);
        return;
      }
      const kmplTouched = kmpl !== row.origKmpl || row.confirmKmpl;
      const patch = { traslado_mxn_per_km: value, km_per_liter: kmpl };
      // A human typed or confirmed it: no longer a spec-sheet guess.
      if (kmplTouched) patch.km_per_liter_estimated = false;
      const { error: err } = await supabase
        .from("car_models")
        .update(patch)
        .eq("id", id);
      if (err) {
        setError("Error guardando: " + err.message);
        setSaving(false);
        return;
      }
    }
    setSaving(false);
    setSaved(true);
    await load();
  };

  // Effective rate + where it came from. Kept explicit in the UI because the
  // whole point of this screen is that an inherited rate is a guess.
  const resolve = (r) => {
    const own = r.rate !== null && String(r.rate).trim() !== "";
    if (own) return { value: Number(r.rate), origen: "explicita" };
    if (r.categoriaRate !== null)
      return { value: Number(r.categoriaRate), origen: "heredada" };
    return { value: null, origen: "ninguna" };
  };

  const missing = rows.filter((r) => resolve(r).origen === "ninguna");
  const inherited = rows.filter((r) => resolve(r).origen === "heredada");

  const originBadge = (origen) => {
    if (origen === "explicita") return <Badge tone="success">Explícita</Badge>;
    if (origen === "heredada") return <Badge tone="attention">Heredada</Badge>;
    return <Badge tone="critical">Sin tarifa</Badge>;
  };

  const content = () => {
    if (loading)
      return (
        <div style={{ padding: 40, textAlign: "center" }}>
          <Spinner size="small" />
        </div>
      );

    return (
      <BlockStack gap="400">
        {error && (
          <Banner tone="critical" onDismiss={() => setError(null)}>
            {error}
          </Banner>
        )}
        {saved && !edited.length && (
          <Banner tone="success" onDismiss={() => setSaved(false)}>
            Tarifas guardadas.
          </Banner>
        )}
        {missing.length > 0 && (
          <Banner tone="warning">
            {missing.length} modelo(s) sin tarifa. No se puede estimar un
            traslado para estos autos hasta capturarla.
          </Banner>
        )}
        {inherited.length > 0 && (
          <Banner tone="info">
            {inherited.length} modelo(s) usan la tarifa de su categoría. Es una
            suposición — captura la tarifa real del modelo.
          </Banner>
        )}

        <LegacyCard title="Precio de combustible" sectioned>
          <BlockStack gap="200">
            <Text tone="subdued">
              Costo real por litro, actualizado cada día a las 7:00 con la
              mediana nacional publicada por la CRE (Gasolina = Premium). No es
              el precio que se cobra al cliente.
            </Text>
            {fuels.map((f) => (
              <InlineStack key={f.id} gap="400" blockAlign="center" wrap={false}>
                <div style={{ width: 90 }}>
                  <Text fontWeight="semibold">{f.name}</Text>
                </div>
                {f.price != null ? (
                  <Text>
                    ${f.price.toFixed(2)}/L{" "}
                    <Text as="span" tone="subdued" variant="bodySm">
                      al {f.since}
                    </Text>
                  </Text>
                ) : (
                  <Badge tone="critical">Sin precio</Badge>
                )}
              </InlineStack>
            ))}
          </BlockStack>
        </LegacyCard>

        <LegacyCard>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Marca</TableCell>
                  <TableCell>Modelo</TableCell>
                  <TableCell>Categoría</TableCell>
                  <TableCell align="right">Autos</TableCell>
                  <TableCell align="right">Tarifa $/km</TableCell>
                  <TableCell align="right">Efectiva</TableCell>
                  <TableCell>Origen</TableCell>
                  <TableCell>Combustible</TableCell>
                  <TableCell align="right">km/L</TableCell>
                  <TableCell />
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((r) => {
                  const { value, origen } = resolve(r);
                  return (
                    <TableRow key={r.id}>
                      <TableCell>{r.marca}</TableCell>
                      <TableCell>{r.modelo}</TableCell>
                      <TableCell>{r.categoria}</TableCell>
                      <TableCell align="right">{r.autos}</TableCell>
                      <TableCell align="right" sx={{ minWidth: 140 }}>
                        <TextField
                          label=""
                          labelHidden
                          type="number"
                          value={r.rate === null ? "" : String(r.rate)}
                          placeholder="—"
                          autoComplete="off"
                          onChange={(v) => changeRate(r.id, v)}
                        />
                      </TableCell>
                      <TableCell align="right">
                        {value === null ? "—" : `$${value.toFixed(2)}`}
                      </TableCell>
                      <TableCell>{originBadge(origen)}</TableCell>
                      <TableCell>{r.combustible}</TableCell>
                      <TableCell align="right" sx={{ minWidth: 120 }}>
                        <TextField
                          label=""
                          labelHidden
                          type="number"
                          value={r.kmpl === null ? "" : String(r.kmpl)}
                          placeholder="—"
                          autoComplete="off"
                          onChange={(v) => changeKmpl(r.id, v)}
                        />
                      </TableCell>
                      <TableCell>
                        {r.kmpl === null || r.kmpl === "" ? (
                          <Badge tone="critical">Sin km/L</Badge>
                        ) : r.kmplEstimated && !r.confirmKmpl && r.kmpl === r.origKmpl ? (
                          <InlineStack gap="100" blockAlign="center" wrap={false}>
                            <Badge tone="attention">Estimado</Badge>
                            <Button variant="plain" onClick={() => confirmKmpl(r.id)}>
                              Confirmar
                            </Button>
                          </InlineStack>
                        ) : null}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </LegacyCard>

        <Text tone="subdued" variant="bodySm">
          La tarifa del modelo gana sobre la de su categoría. Dejar el campo
          vacío borra la tarifa del modelo. Los km/L marcados "Estimado" vienen
          de la ficha técnica; confírmalos o corrígelos con el rendimiento real.
        </Text>
      </BlockStack>
    );
  };

  if (!allowed)
    return (
      <AppWrapper>
        <Page title="Tarifas de Traslado" backAction={{ url: "/vehicle_transfers" }}>
          <Banner tone="warning">Solo quien autoriza pagos puede ver y editar las tarifas.</Banner>
        </Page>
      </AppWrapper>
    );

  return (
    <AppWrapper>
      <Page
        title="Tarifas de Traslado"
        fullWidth
        subtitle="Pago al chofer por km, rendimiento por modelo y precio de combustible"
        backAction={{ url: "/vehicle_transfers" }}
        primaryAction={
          <Button
            variant="primary"
            loading={saving}
            disabled={!edited.length || saving}
            onClick={save}
          >
            {edited.length ? `Guardar (${edited.length})` : "Guardar"}
          </Button>
        }
      >
        {content()}
      </Page>
    </AppWrapper>
  );
}

export default TrasladoRates;
